import React, { useState } from 'react';
import { Col, Label, Row } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory from 'react-bootstrap-table2-paginator';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useOpenAdverseEvents } from '../../containers/MedicalRecords/Hooks/useAdverseEvents';
import { conditions } from '../../variables/Enums';
import { CustomSearchBox } from '../CustomControls/CustomSearchBox';
import { NullableDatePickerCombo } from '../CustomControls/DatePicker';
import { ExpandableContent } from '../CustomControls/ExpandableContent';
import { Add, View } from '../Icons/Icons';
import { dateFormatter, yesNoFormatter } from '../Utils/Formatter';
import { Tooltip } from '../Utils/Tooltips';
import { EntryItemsTable } from './EntryItemsTable';

export const AdverseEvents = (props) => {
    const { t } = useTranslation();

    function _getCategory(item) {
        const chronic = item.isChronic ? ` ${t("medicalRecords.condition.chronic")}` : "";
        if (item.category === conditions.diagnosis)
            return (<span className="item-text">({t("medicalRecords.condition.diagnosis")}{chronic}) </span>)
        if (item.isPersonalHistory)
            return (<span className="item-text">({t("medicalRecords.condition.personalHistory")}{chronic}) </span>)
    }

    const columns = [
        {
            dataField: 'adverseEffect', text: t('medicalRecords.adverseEvents.adverseEvent'), formatter: function (_cell, row, rowindex) {
                return (

                    <Row key={`adverseEvent-item-${rowindex}`} style={{ textAlign: 'left' }} className="item-data">
                        <Col md={8}>
                            {_getCategory(row)}
                            <span className="item-text">{row.adverseEffect}</span>
                            {
                                (row.from || row.to) &&
                                <div>
                                    {row.from && <span><span className="item-label">{t('medicalRecords.from')}:</span> <span className="item-text">{row.from}</span></span>}
                                    {row.from && row.to && <span>&nbsp;&nbsp;</span>}
                                    {row.to && <span><span className="item-label">{t('medicalRecords.to')}:</span> <span className="item-text">{row.to}</span></span>}
                                </div>
                            }
                        </Col>
                        <Col md={4} style={{ textAlign: 'right' }}>
                            <div><span className="item-label">{t('medicalRecords.itemDates.created')}:</span> <span className="item-text">{dateFormatter(row.created, "DD/MM/YY")}</span></div>
                            {
                                moment(row.modified).format("DD/MM/YY") !== moment(row.created).format("DD/MM/YY") &&
                                <div><span className="item-label">{t('medicalRecords.itemDates.modified')}:</span> <span className="item-text">{dateFormatter(row.modified, "DD/MM/YY")}</span></div>
                            }
                            {row.toYear && row.entryId == props.entryId &&
                                <div><Label bsStyle="danger">{t('medicalRecords.itemDates.closedHere')}</Label></div>
                            }
                        </Col>
                    </Row>
                )
            }
        },
    ];

    const customActions = function (row, props) {
        if (props.onAddDiagnosis && props.entryId != row.entryId)
            return <Tooltip tooltip={t("medicalRecords.adverseEvents.addDiagnosis")} id={"tooltipadddiagnosis-" + row.id}>
                <button className="btn-table-actions" onClick={() => props.onAddDiagnosis(row)}>
                    <Add />
                </button>
            </Tooltip>
    }

    const expandedContent = (row) => (
        <div className="info">
            {row.intensity && <div><span className="patient-label">{t('medicalRecords.adverseEvents.intensity')}:</span> <span className="patient-text">{row.intensity.title ?? row.intensity}</span></div>}
            {row.action && <div><span className="patient-label">{t('medicalRecords.adverseEvents.action')}:</span> <span className="patient-text">{row.action}</span></div>}
            {row.serious && <div><span className="patient-label">{t('medicalRecords.adverseEvents.serious')}:</span> <span className="patient-text">{yesNoFormatter(row.serious, t)}</span></div>}
            {row.relationwithdrug && <div><span className="patient-label">{t('medicalRecords.adverseEvents.relationwithdrug')}:</span> <span className="patient-text">{row.relationwithdrug}</span></div>}
            {row.reported && <div><span className="patient-label">{t('medicalRecords.adverseEvents.reported')}:</span> <span className="patient-text">{yesNoFormatter(row.reported, t)}</span></div>}
            {row.notes && <div><span className="patient-label">{t('medicalRecords.adverseEvents.notes')}:</span> <span className="patient-text">{row.notes}</span></div>}
        </div>
    )

    const rowClasses = (row) => { return row.logicalItemId && row.logicalItemId !== row.id ? 'item-modified' : ''; };

    return (
        <>
            {props.onSearchTermChange && <CustomSearchBox handleSearch={props.onSearchTermChange} placeholder={t('medicalRecords.searchByName')} />}
            <EntryItemsTable columns={columns} customActions={customActions} {...props} rowClasses={rowClasses} expandContent={expandedContent} />
        </>
    );
}

export const AdverseEventsOpenItems = (props) => {
    const { value, medicalRecordNumber, patientNumber, expandedAll, isDisabled, title, protocolId, visitId } = props;
    const [adverseEvents, query, totalSize, isLoading, handleTableChange] = useOpenAdverseEvents(medicalRecordNumber, null, protocolId, visitId);
    const [closedItems, setClosedItems] = useState(value !== "" && value !== null ? value : []);
    const { t } = useTranslation();

    const handleChange = (item, name, value) => {
        let dateName = name.split(".")[1];
        var closedItem = closedItems.find(x => x.id === item.id);

        if (closedItem) { // Update
            closedItem.closed[dateName] = value;

            if (!closedItem.closed.toDay && !closedItem.closed.toMonth && !closedItem.closed.toYear) {
                const closedItemsCopy = closedItems.filter(x => x.id !== closedItem.id); // Remove
                setClosedItems(closedItemsCopy);
                props.onChange(props.name, closedItemsCopy);
            } else {
                let closedItemsCopy = JSON.parse(JSON.stringify(closedItems));
                var index = closedItemsCopy.findIndex(x => x.id === item.id);
                closedItemsCopy.splice(index, 1, closedItem); // Update. 
                setClosedItems(closedItemsCopy);
                props.onChange(props.name, closedItemsCopy);
            }
        }
        else {
            // New
            var joined = closedItems.concat({
                id: item.id,
                closed: { [dateName]: value },
                name: item.adverseEffect
            });
            setClosedItems(joined);
            props.onChange(props.name, joined);
        }
    }

    const handleBlur = () => {
        if (props.onBlur)
            props.onBlur(props.name, true);
    };

    const columns = [
        { dataField: 'adverseEffect', text: t('medicalRecords.adverseEvents.adverseEvent') },
        //{ dataField: 'id', text: 'id' },
        //{ dataField: 'originalId', text: 'originalId' },
        //{ dataField: 'visitId', text: 'visitId' },
        //{ dataField: 'version', text: 'version' },
        { dataField: 'from', text: t('medicalRecords.from') },
        {
            isDummyField: true,
            dataField: 'to',
            text: t('medicalRecords.to'),
            formatter: function (_cell, row, rowindex) {
                var closedItem = closedItems?.find(x => x.id === row.id);
                const isClosed = row.to !== "" && row.to !== null && row.toYear !== "" && row.toYear !== null;
                if (isClosed) return "";
                return (<span className="item-text">
                    <NullableDatePickerCombo
                        nameDay={`${rowindex}.toDay`}
                        nameMonth={`${rowindex}.toMonth`}
                        nameYear={`${rowindex}.toYear`}
                        onChange={(name, value) => handleChange(row, name, value)}
                        valueDay={closedItem ? closedItem.closed.toDay : row.toDay}
                        valueMonth={closedItem ? closedItem.closed.toMonth : row.toMonth}
                        valueYear={closedItem ? closedItem.closed.toYear : row.toYear}
                        onBlur={handleBlur}
                        disabled={isDisabled}
                    />
                </span>)
            }
        },
        {
            dataField: 'actions',
            text: t("commons.actions"),
            isDummyField: true,
            headerClasses: 'header-actions',
            classes: 'row-actions',
            formatter: function (_cell, row, rowIndex) {
                if (!protocolId || !patientNumber || !medicalRecordNumber)
                    return;
                return (
                    <div key={`${Math.random()}-actions-${rowIndex}`}>
                        {
                            row.visitId ?
                                <Tooltip tooltip={t("medicalRecords.viewVisit")} id={"tooltipview-" + row.id}>
                                    <NavLink to={`/admin/protocols/${row.protocolId}/visitTracking/${row.visitId}/patient/${row.numberInProtocol}`} className="nav-link" activeClassName="active">
                                        <View />
                                    </NavLink>
                                </Tooltip>
                                :
                                <Tooltip tooltip={t("medicalRecords.viewVisit")} id={"tooltipview-" + row.id}>
                                    <NavLink to={`/admin/medicalRecords/${medicalRecordNumber}/entry/${row.entryId}`} className="nav-link" activeClassName="active">
                                        <View />
                                    </NavLink>
                                </Tooltip>
                        }
                    </div>
                )
            }
        }
    ];

    const columnsClosed = [
        //{ dataField: 'id', text: "id" },
        { dataField: 'name', text: t('medicalRecords.adverseEvents.adverseEvent') },
        {
            isDummyField: true,
            dataField: 'to',
            text: t('medicalRecords.to'),
            formatter: function (_cell, row, rowindex) {
                if (!row.closed) return "";

                return (
                    <span className="item-text">
                        <NullableDatePickerCombo
                            nameDay={`${rowindex}.toDay`}
                            nameMonth={`${rowindex}.toMonth`}
                            nameYear={`${rowindex}.toYear`}
                            onChange={(name, value) => handleChange(row, name, value)}
                            onBlur={handleBlur}
                            valueDay={row.closed.toDay}
                            valueMonth={row.closed.toMonth}
                            valueYear={row.closed.toYear}
                            disabled={isDisabled}
                        />
                    </span>
                )
            }
        }
    ];

    // En VALUE, vienen todos los Items cerrados en el form.
    // En adverseEvents, vienen todos los items abiertos de la HC.
    return (
        <>
            {
                value && value.length > 0 &&
                <ExpandableContent title={t("medicalRecords.adverseEvents.closed")} expandedAll={expandedAll} content="adverseEvents">
                    <BootstrapTable
                        bordered={false}
                        classes="table-spaced table-spaced-small"
                        wrapperClasses="table-responsive"
                        striped
                        hover
                        keyField='id'
                        data={value ? value : []}
                        columns={columnsClosed}
                    />
                </ExpandableContent>
            }
            <ExpandableContent title={title} expandedAll={expandedAll} content="adverseEvents">
                <BootstrapTable
                    bordered={false}
                    classes="table-spaced table-spaced-small"
                    wrapperClasses="table-responsive"
                    striped
                    hover
                    remote
                    keyField='id'
                    data={adverseEvents ? adverseEvents : []}
                    columns={columns}
                    pagination={query && query.pagination && paginationFactory({
                        page: query.pagination.page,
                        sizePerPage: query.pagination.sizePerPage,
                        totalSize: totalSize,
                        hideSizePerPage: true,
                        hidePageListOnlyOnePage: true
                    })}
                    onTableChange={handleTableChange}
                    noDataIndication={() => <>{t("commons.noData")}</>}
                    loading={isLoading}
                    overlay={overlayFactory({ spinner: true })}
                />
            </ExpandableContent>
        </>
    );
}